import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = { name:'', department:'', designation:'', phone:'', email:'', employee_code:'', sub_department:'', status:'active', company_id:'' };
const DEPARTMENTS = ['HR','Finance','IT','Admin','Operations','Marketing','CEO Office','Sales','Legal','Engineering','SOCKS OPERATIONS','Finishing','Knitting','Production','Other'];

// Map Excel columns to our DB fields
const mapExcelRow = (row) => {
  const firstName = (row['FirstName'] || row['first_name'] || row['Employee Name'] || '').toString().trim();
  const lastName  = (row['LastName']  || row['last_name']  || '').toString().trim();
  const name = lastName ? `${firstName} ${lastName}`.trim() : firstName;
  return {
    employee_code:  (row['EmployeeCode']  || row['Employee ID']   || row['employee_code'] || '').toString().trim(),
    name:           name || (row['EmployeeName'] || row['name'] || '').toString().trim(),
    department:     (row['Department']    || row['department']    || '').toString().trim(),
    sub_department: (row['SubDepartment'] || row['sub_department']|| '').toString().trim(),
    designation:    (row['Designation']   || row['designation']   || '').toString().trim(),
    phone:          (row['MobileNo']      || row['Phone']         || row['phone'] || '').toString().trim(),
    email:          (row['Email']         || row['email']         || '').toString().trim(),
    unit_name:      (row['Station']       || row['Unit']          || row['unit']  || '').toString().trim(),
    status:         ((row['Status'] || row['status'] || 'Active').toString().trim().toLowerCase() === 'active') ? 'active' : 'inactive',
  };
};

export default function Employees() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [employees, setEmployees]   = useState([]);
  const [companies, setCompanies]   = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(EMPTY);
  const [editing, setEditing]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('active');

  // Import state
  const [importing, setImporting]       = useState(false);
  const [importPreview, setImportPreview] = useState(null); // {rows[], errors[]}
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/employees');
      setEmployees(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
    if (user?.role === 'admin') {
      try {
        const cr = await api.get('/api/companies');
        setCompanies(cr.data);
      } catch (err) { console.error(err); }
    }
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const filtered = employees.filter(e => {
    const matchSearch = e.name.toLowerCase().includes(search.toLowerCase()) ||
      (e.department||'').toLowerCase().includes(search.toLowerCase()) ||
      (e.designation||'').toLowerCase().includes(search.toLowerCase()) ||
      (e.employee_code||'').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || e.status === filterStatus || (!e.status && filterStatus === 'active');
    return matchSearch && matchStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/api/employees/${editing}`, form);
      else await api.post('/api/employees', form);
      setShowForm(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!confirmDelete('employee', name)) return;
    try { await api.delete(`/api/employees/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  const handleEdit = (emp) => {
    setForm({ name:emp.name, department:emp.department||'', designation:emp.designation||'',
              phone:emp.phone||'', email:emp.email||'', employee_code:emp.employee_code||'',
              sub_department:emp.sub_department||'', status:emp.status||'active',
              company_id: emp.company_id || '' });
    setEditing(emp.id); setShowForm(true);
  };

  // ── EXCEL IMPORT ──────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const XLSX = window.XLSX;
        if (!XLSX) { alert('Excel library not loaded. Please refresh and try again.'); return; }
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });

        const rows = [];
        const errors = [];
        raw.forEach((row, i) => {
          const mapped = mapExcelRow(row);
          if (!mapped.name) { errors.push(`Row ${i+2}: Name is empty`); return; }
          rows.push(mapped);
        });

        setImportPreview({ rows, errors, filename: file.name });
        setImporting(true);
      } catch (err) {
        alert('Failed to read Excel file: ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const handleImportConfirm = async () => {
    if (!importPreview?.rows?.length) return;
    setImportLoading(true);
    try {
      const res = await api.post('/api/employees/import', { employees: importPreview.rows });
      setImportResult(res.data);
      setImportPreview(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Import failed');
    } finally { setImportLoading(false); }
  };

  const handleImportCancel = () => {
    setImporting(false);
    setImportPreview(null);
    setImportResult(null);
  };

  // ── EXPORT CSV ────────────────────────────────────────────────
  const handleExportCSV = () => {
    const header = 'Employee Code,Name,Department,Sub Department,Designation,Phone,Email,Status\n';
    const rows = filtered.map(e =>
      `"${e.employee_code||''}","${e.name}","${e.department||''}","${e.sub_department||''}","${e.designation||''}","${e.phone||''}","${e.email||''}","${e.status||'active'}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'employees.csv';
    a.click();
  };

  // ── DOWNLOAD TEMPLATE ─────────────────────────────────────────
  const handleDownloadTemplate = () => {
    const a = document.createElement('a');
    a.href = '/employee_template.xlsx';
    a.download = 'employee_template.xlsx';
    a.click();
  };

  const activeCount   = employees.filter(e => !e.status || e.status === 'active').length;
  const inactiveCount = employees.filter(e => e.status === 'inactive').length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Employee Management</h1>
          <p className="text-gray-500 text-sm">{activeCount} active · {inactiveCount} inactive</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleDownloadTemplate}
            className="border border-gray-300 bg-white px-3 py-2 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1">
            📥 Sample Template
          </button>
          <button onClick={() => fileInputRef.current.click()}
            className="border border-green-500 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm hover:bg-green-100 flex items-center gap-1 font-medium">
            📊 Import Excel
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileSelect} />
          <button onClick={handleExportCSV}
            className="border border-blue-400 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm hover:bg-blue-100 flex items-center gap-1">
            📤 Export CSV
          </button>
          <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ➕ Add Employee
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, code, department, designation..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]" />
        <div className="flex gap-1">
          {[['active','✅ Active'],['inactive','❌ Inactive'],['all','All']].map(([val,label]) => (
            <button key={val} onClick={() => setFilterStatus(val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterStatus===val ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── IMPORT PREVIEW MODAL ── */}
      {importing && importPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Import Preview</h2>
                <p className="text-sm text-gray-500">{importPreview.filename} — {importPreview.rows.length} valid rows</p>
              </div>
              <button onClick={handleImportCancel} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>

            {importPreview.errors.length > 0 && (
              <div className="mx-5 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                <strong>⚠️ {importPreview.errors.length} row(s) skipped:</strong>
                <ul className="mt-1 list-disc list-inside max-h-20 overflow-y-auto">
                  {importPreview.errors.map((e,i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>{['Code','Name','Department','Sub Dept','Designation','Phone','Unit','Status'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 border-b">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {importPreview.rows.slice(0,100).map((r,i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-gray-500">{r.employee_code||'—'}</td>
                      <td className="px-3 py-1.5 font-medium text-gray-800">{r.name}</td>
                      <td className="px-3 py-1.5 text-gray-600">{r.department||'—'}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.sub_department||'—'}</td>
                      <td className="px-3 py-1.5 text-gray-600">{r.designation||'—'}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.phone||'—'}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.unit_name||'—'}</td>
                      <td className="px-3 py-1.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status==='active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {importPreview.rows.length > 100 && (
                    <tr><td colSpan={8} className="px-3 py-2 text-center text-gray-400 text-xs">...and {importPreview.rows.length - 100} more rows</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-5 border-t flex gap-3 justify-between items-center">
              <p className="text-sm text-gray-500">Duplicates (same code) will be skipped automatically.</p>
              <div className="flex gap-2">
                <button onClick={handleImportCancel} className="border border-gray-300 px-5 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleImportConfirm} disabled={importLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                  {importLoading ? '⏳ Importing...' : `✅ Import ${importPreview.rows.length} Employees`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORT RESULT MODAL ── */}
      {importResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Import Complete</h2>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-blue-700">{importResult.total}</p>
                <p className="text-xs text-gray-500 mt-1">Total Rows</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-green-700">{importResult.imported}</p>
                <p className="text-xs text-gray-500 mt-1">Imported</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-yellow-700">{importResult.skipped}</p>
                <p className="text-xs text-gray-500 mt-1">Skipped</p>
              </div>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 rounded-lg text-left text-xs text-red-700 max-h-32 overflow-y-auto">
                {importResult.errors.map((e,i) => <p key={i}>{e}</p>)}
              </div>
            )}
            <button onClick={() => { setImportResult(null); setImporting(false); }}
              className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-2.5 rounded-lg font-medium">
              Done
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Employee' : 'Add Employee'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee Code</label>
                <input type="text" value={form.employee_code} onChange={e => setForm({...form,employee_code:e.target.value})}
                  placeholder="e.g. U1-0001"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm({...form,status:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input type="text" value={form.name} onChange={e => setForm({...form,name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {isAdmin ? (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company <span className="text-red-500">*</span></label>
                  <select required value={form.company_id} onChange={e => setForm({...form, company_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">-- Select Company --</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500">Auto-assigned to your company</div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select value={form.department} onChange={e => setForm({...form,department:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select —</option>
                  {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sub Department</label>
                <input type="text" value={form.sub_department} onChange={e => setForm({...form,sub_department:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                <input type="text" value={form.designation} onChange={e => setForm({...form,designation:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={form.phone} onChange={e => setForm({...form,phone:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({...form,email:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Employee'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Employee Cards */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-400">
          <div className="text-5xl mb-3">👔</div>
          <p className="font-medium">No employees found</p>
          <p className="text-sm mt-1">Try adjusting your search or import from Excel</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500">Showing {filtered.length} of {employees.length} employees</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(emp => (
              <div key={emp.id} className={`bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow ${emp.status === 'inactive' ? 'opacity-60 border-gray-100' : 'border-gray-100'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white ${emp.status === 'inactive' ? 'bg-gray-400' : 'bg-blue-600'}`}>
                      {emp.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{emp.name}</h3>
                      <p className="text-sm text-gray-500">{emp.designation || 'N/A'}</p>
                      {emp.employee_code && <p className="text-xs text-gray-400 font-mono">{emp.employee_code}</p>}
                      {isAdmin && emp.company_name && <p className="text-xs text-blue-500 font-medium">{emp.company_name}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(emp)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-blue-600">✏️</button>
                    {isAdmin && <button onClick={() => handleDelete(emp.id, emp.name)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500">🗑️</button>}
                  </div>
                </div>
                <div className="mt-4 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{emp.department || 'No Dept'}</span>
                    {emp.sub_department && <span className="px-2 py-0.5 bg-gray-50 text-gray-600 rounded text-xs">{emp.sub_department}</span>}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${emp.status === 'inactive' ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'}`}>
                      {emp.status === 'inactive' ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                  {emp.phone && <div className="flex items-center gap-2 text-sm text-gray-600"><span>📞</span>{emp.phone}</div>}
                  {emp.email && <div className="flex items-center gap-2 text-sm text-gray-600 truncate"><span>✉️</span><span className="truncate">{emp.email}</span></div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
